import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ChatHistoryService from '../services/chatHistoryService';
import { trackChatAPI } from '../utils/apiTracker';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

function getBusinessLabelFromType(type, t) {
  if (!type || typeof type !== 'string') return type || '';
  const trimmed = type.trim();
  const camelKey = trimmed.split(/\s+/).map((w, i) =>
    i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join('');
  const key = 'business.' + camelKey;
  const translated = t(key);
  return (translated && translated !== key) ? translated : trimmed;
}

export const useChatActions = (setMessages, setBusinessType, currentConversationId, setCurrentConversationId, setCurrentSessionId, businessType, messages, setIsLoading, saveChat, user, refreshUserData, inputMode = 'keyboard') => {
  const { isAuthenticated, updateProfile } = useAuth();
  const { currentLanguage, t } = useLanguage();
  
  const handleSendMessage = async (message, currentInputMode = inputMode) => {
    if (!message || typeof message !== 'string' || !message.trim()) return;
    
    // Add user message to chat
    const userMessage = { type: 'user', content: message, timestamp: new Date() };
    console.log('💬 useChatActions: Added to chat:', message);
    console.log('💬 Current conversation ID:', currentConversationId);
    console.log('💬 Messages length:', messages.length);
    console.log('💬 Is authenticated:', isAuthenticated());
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Only send conversationId if chatbox has messages (not empty)
      // Empty chatbox = new conversation, so backend will create it
      // Backend will check if conversation is empty and create new one if needed
      let conversationId = currentConversationId; // Local variable that can be updated
      const shouldSendConversationId = messages.length > 0 && conversationId;
      console.log('🔍 Conversation ID check:', { 
        currentConversationId, 
        conversationId,
        messagesLength: messages.length,
        shouldSendConversationId,
        isAuthenticated: isAuthenticated() 
      });

      // Send message to AI
      const authToken = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const API_BASE_URL = getApiBaseUrl();
      const conversationHistory = messages
        .filter(item => item && typeof item.content === 'string')
        .slice(-12)
        .map(item => ({
          role: item.type === 'user' ? 'user' : 'assistant',
          content: item.content
        }));
      
      const chatUrl = `${API_BASE_URL}/api/chat`;
      console.log('💬 Using API URL:', API_BASE_URL, 'from env:', process.env.REACT_APP_API_URL);
      console.log('💬 Sending chat request to:', chatUrl);
      
      // Use current language from context
      
      console.log('💬 Sending chat request:', {
        url: chatUrl,
        message: message.substring(0, 50),
        businessType,
        language: currentLanguage,
        conversationId: shouldSendConversationId ? conversationId : null,
        hasAuth: !!authToken
      });
      
      const response = await trackChatAPI(chatUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          message, 
          language: currentLanguage, 
          businessType, 
          userId: isAuthenticated() ? (authToken ? 1 : null) : null,
          conversationId: shouldSendConversationId ? currentConversationId : null, // Only send if chatbox has messages
          conversationHistory
        })
      });
      
      console.log('💬 Chat response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details');
        console.error('💬 Chat request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText.substring(0, 100)}`);
      }
      
      const data = await response.json();
      console.log('💬 Chat response data:', {
        hasResponse: !!data.response,
        responseLength: data.response?.length || 0,
        conversationId: data.conversationId,
        title: data.title,
        fullData: data
      });
      
      // Handle different response formats
      if (!data) {
        console.error('💬 Empty response from server');
        throw new Error('Empty response from server');
      }
      
      // Check for error in response
      if (data.error) {
        console.error('💬 Server returned error:', data.error);
        throw new Error(data.error || 'Server error');
      }
      
      // Response might be in 'response' or 'message' field
      const aiResponse = data.response || data.message;
      if (!aiResponse) {
        console.error('💬 Invalid response format - no response/message field:', data);
        throw new Error('Invalid response format from server - no response field');
      }
      
      // Update data.response if we used data.message
      if (!data.response && data.message) {
        data.response = data.message;
      }
      
      // Update conversation ID if provided by backend
      if (data.conversationId && data.conversationId !== conversationId) {
        console.log('🔄 Updating conversation ID from backend:', data.conversationId);
        setCurrentConversationId(data.conversationId);
        setCurrentSessionId(data.conversationId);
        conversationId = data.conversationId;
      }
      
      const botMessage = { 
        type: 'answer', 
        content: data.response, 
        timestamp: new Date(),
        source: currentInputMode === 'voice' ? 'fromVoiceToText' : 'fromKeyboardToText'
      };
      setMessages(prev => [...prev, botMessage]);

      // Note: Messages are already saved by the backend in chatController.js
      // No need to save again here to prevent duplicates
      
      // Update conversation title if provided by backend (for new conversations)
      if (data.conversationId && data.title && messages.length === 0 && isAuthenticated()) {
        try {
          await ChatHistoryService.updateConversationTitle(data.conversationId, data.title);
          console.log('Updated conversation title to:', data.title);
        } catch (titleUpdateError) {
          console.error('Failed to update conversation title:', titleUpdateError);
        }
      }
      
      // Fallback: Only create conversation if backend didn't create one
      // Note: Don't save messages here - backend already saved them when processing the chat request
      if (isAuthenticated() && !conversationId && !data.conversationId) {
        // Fallback: Create conversation now if it wasn't created initially
        try {
          console.log('Creating conversation as fallback after AI response...');
          const conversation = await ChatHistoryService.createConversation(
            businessType || 'general', 
            'en',
            message,
            message
          );
          
          if (conversation && conversation.id) {
            setCurrentConversationId(conversation.id);
            setCurrentSessionId(conversation.id);
            // Messages are already saved by backend in chatController.js
            // No need to save again here to prevent duplicates
            console.log('Fallback conversation created:', conversation.id);
          }
        } catch (fallbackError) {
          console.error('Failed to create fallback conversation:', fallbackError);
        }
      }
      
      // Fallback: Save chat to localStorage for chat history only if no conversation exists
      if (saveChat && !conversationId) {
        saveChat(message, data.response, businessType || 'general', 'en');
      }
      
    } catch (error) {
      console.error('❌ Error sending message:', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        type: error.constructor.name
      });
      
      // Provide more specific error messages
      let errorContent = 'Sorry, I\'m having trouble connecting. Please try again.';
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorContent = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        errorContent = 'Request timed out. Please try again.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorContent = 'Authentication required. Please log in and try again.';
      } else if (error.message.includes('500')) {
        errorContent = 'Server error. Please try again in a moment.';
      } else if (error.message) {
        errorContent = `Error: ${error.message.substring(0, 100)}`;
      }
      
      const errorMessage = { 
        type: 'answer', 
        content: errorContent, 
        timestamp: new Date(),
        source: currentInputMode === 'voice' ? 'fromVoiceToText' : 'fromKeyboardToText'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickOption = (option) => {
    console.log('🔄 useChatActions: Processing popup option:', option);
    const message = `${t('quickOptions.iNeedHelpWith')} ${option}. ${t('quickOptions.canYouProvideGuidance')}`;
    handleSendMessage(message);
  };

  const handleBusinessTypeSelect = async (type) => {
    try {
      const userHasBusinessType = user?.business_type && user.business_type.trim() !== '';
      
      if (userHasBusinessType) {
        // Adding a second business when user already has a primary business
        const userMessage = { type: 'user', content: `I also own a ${type}`, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        
        const botMessage = { type: 'question', content: `Great! You now have both ${type} and another business. What would you like to know about running your ${type} in Oakland?`, timestamp: new Date() };
        setMessages(prev => [...prev, botMessage]);
        
        setBusinessType(type);
      } else {
        // Setting primary business for the first time
        setBusinessType(type);
        const userMessage = { type: 'user', content: `I own a ${type}`, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        
        const botMessage = { type: 'question', content: 'Great! What would you like to know about running your business in Oakland?', timestamp: new Date() };
        setMessages(prev => [...prev, botMessage]);
        
        if (isAuthenticated()) {
          try {
            const result = await updateProfile({ businessType: type });
            if (result.success) {
              console.log('Business type set successfully:', type);
            }
          } catch (error) {
            console.error('Failed to set business type in profile:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error handling business type selection:', error);
    }
  };



  const handleAddSecondBusiness = async (businessType) => {
    try {
      console.log('🚀 handleAddSecondBusiness called with type:', businessType);
      
      if (isAuthenticated()) {
        console.log('🚀 User is authenticated, calling updateProfile...');
        const result = await updateProfile({ secondBusinessType: businessType });
        console.log('🚀 updateProfile result:', result);
        
        if (result.success) {
          console.log('✅ Second business added successfully:', businessType);
          
          // Refresh user data to get updated business type
          if (refreshUserData) {
            console.log('🔄 Calling refreshUserData...');
            refreshUserData();
          }
          
          // Add success message to chat
          const userMessage = { 
            type: 'user', 
            content: `I've added ${businessType} as my second business`, 
            timestamp: new Date() 
          };
          setMessages(prev => [...prev, userMessage]);
          
          const botMessage = { 
            type: 'question', 
            content: `Excellent! You now have both ${result.user.business_type} and ${businessType}. What would you like to know about running your ${businessType} in Oakland?`, 
            timestamp: new Date() 
          };
          setMessages(prev => [...prev, botMessage]);
          
          // Switch to the new business type
          setBusinessType(businessType);
        } else {
          console.log('❌ updateProfile failed:', result);
        }
      } else {
        console.log('❌ User is not authenticated');
      }
    } catch (error) {
      console.error('❌ Failed to add second business:', error);
    }
  };

  const handleRemoveSecondBusiness = async () => {
    try {
      console.log('🗑️ handleRemoveSecondBusiness called');
      
      if (isAuthenticated()) {
        const result = await updateProfile({ secondBusinessType: '' });
        console.log('🗑️ updateProfile result:', result);
        
        if (result.success) {
          console.log('Second business removed successfully');
          
          // Refresh user data to get updated business type
          if (refreshUserData) {
            refreshUserData();
          }
          
          const userMessage = { type: 'user', content: `I've removed ${result.user.second_business_type || 'your second business'} as my second business`, timestamp: new Date() };
          setMessages(prev => [...prev, userMessage]);
          
          const botMessage = { type: 'question', content: `Got it! You now only have your ${result.user.business_type}. What would you like to know about running your business in Oakland?`, timestamp: new Date() };
          setMessages(prev => [...prev, botMessage]);
          
          if (businessType === (result.user.second_business_type || '')) {
            setBusinessType('');
          }
        }
      }
    } catch (error) {
      console.error('Failed to remove second business:', error);
    }
  };

  const handleAddPrimaryBusiness = async (businessType) => {
    try {
      console.log('🚀 handleAddPrimaryBusiness called with type:', businessType);
      
      if (isAuthenticated()) {
        console.log('🚀 User is authenticated, calling updateProfile...');
        const result = await updateProfile({ businessType: businessType });
        console.log('🚀 updateProfile result:', result);
        
        if (result.success) {
          console.log('✅ Primary business added successfully:', businessType);
          
          // Refresh user data to get updated business type
          if (refreshUserData) {
            console.log('🔄 Calling refreshUserData...');
            refreshUserData();
          }
          
          const businessLabel = getBusinessLabelFromType(businessType, t);
          const userMsg = t('fullchat.setPrimaryBusiness').replace(/\{\{business\}\}/g, businessLabel);
          const userMessage = { type: 'user', content: userMsg, timestamp: new Date() };
          setMessages(prev => [...prev, userMessage]);
          const botMsg = t('fullchat.excellentSetPrimary').replace(/\{\{business\}\}/g, businessLabel);
          const botMessage = { type: 'question', content: botMsg, timestamp: new Date() };
          setMessages(prev => [...prev, botMessage]);
          
          // Set the business type
          setBusinessType(businessType);
        } else {
          console.log('❌ updateProfile failed:', result);
        }
      } else {
        console.log('❌ User is not authenticated');
      }
    } catch (error) {
      console.error('❌ Failed to add primary business:', error);
    }
  };

  const handleRemovePrimaryBusiness = async () => {
    try {
      console.log('🗑️ handleRemovePrimaryBusiness called');
      console.log('🗑️ Current user:', user);
      console.log('🗑️ Is authenticated:', isAuthenticated());
      
      if (isAuthenticated()) {
        console.log('🗑️ Calling updateProfile with businessType: ""');
        // Try with empty string
        const result = await updateProfile({ businessType: '' });
        console.log('🗑️ updateProfile result:', result);
        
        if (result && result.success) {
          console.log('✅ Primary business removed successfully');
          console.log('✅ Updated user data:', result.user);
          
          // Force immediate UI update by clearing business type
          setBusinessType('');
          
          // Refresh user data to get updated business type
          if (refreshUserData) {
            console.log('🔄 Calling refreshUserData...');
            await refreshUserData();
          }
          
          const userMessage = { type: 'user', content: t('fullchat.removedPrimaryBusiness'), timestamp: new Date() };
          setMessages(prev => [...prev, userMessage]);
          const botMessage = { type: 'question', content: t('fullchat.gotItNoPrimary'), timestamp: new Date() };
          setMessages(prev => [...prev, botMessage]);
          
          // Force localStorage update and trigger re-render
          setTimeout(() => {
            console.log('🔄 Manually updating localStorage...');
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            currentUser.business_type = '';
            localStorage.setItem('user', JSON.stringify(currentUser));
            
            // Trigger a custom event to notify components of user data change
            window.dispatchEvent(new CustomEvent('userDataUpdated', { 
              detail: { user: currentUser } 
            }));
          }, 500);
        } else {
          console.log('❌ updateProfile failed or returned no success:', result);
        }
      } else {
        console.log('❌ User is not authenticated');
      }
    } catch (error) {
      console.error('❌ Failed to remove primary business:', error);
    }
  };


  
  return {
    handleSendMessage,
    handleQuickOption,
    handleBusinessTypeSelect,
    handleAddSecondBusiness,
    handleRemoveSecondBusiness,
    handleAddPrimaryBusiness,
    handleRemovePrimaryBusiness
  };
};
